import { liquityBranchesByChain, liquityConfigFor } from '@1delta/data-sdk'
import { parseRawAmount } from '../../../utils/parsing'
import { createMarketUid } from '../../../utils'
import { getDisplayPrice, getOraclePrice } from '../utils/oraclePrice'
import { createMultiAccountTypeUserState } from '../utils'
import {
  LenderCrossPoolMeta,
  LenderToLenderCrossPoolMeta,
  UserData,
} from '../utils/types'
import { liquityLenderKey } from '../../public-data/liquity/convertPublic'
import { getCachedLiquityTroves, liquityBranchCallCount } from './userCallBuild'

/** Per-trove detail attached to the debt position row (raw strings). */
export interface LiquityTroveInfo {
  /** decimal uint256 troveId — the sub-account id */
  troveId: string
  collIndex: number
  /** user-set annual interest rate, WAD string (batch rate when delegated) */
  annualInterestRate: string
  /** unix seconds of the last rate change (premature-adjust fee window) */
  lastInterestRateAdjTime: number
  /** redeemed below min debt — adjust via adjustZombieTrove */
  zombie: boolean
  /** pending redistribution gains (from liquidations shared to the branch) */
  redistCollGain: string
  redistBoldDebtGain: string
  /** interest + batch-management fee accrued into entireDebt */
  accruedInterest: string
  accruedBatchManagementFee: string
}

/** Stability-pool position detail attached to the `sp` sub-account rows. */
export interface LiquitySpInfo {
  /** compounded stable deposit (raw) */
  deposit: string
  /** claimable stable yield incl. pending aggregate interest (raw) */
  yieldGain: string
  /** claimable liquidation collateral gain (raw coll units) */
  collGain: string
  /** collateral stashed from prior doClaim=false ops (raw coll units) */
  stashedColl: string
  /** post-liquidation collateral surplus claimable via claimCollateral (raw) */
  collSurplus: string
}

const big = (v: any): bigint => {
  try {
    if (typeof v === 'bigint') return v
    if (typeof v === 'number' || typeof v === 'string') return BigInt(v)
  } catch {}
  return 0n
}

/** Field access for decoded tuples that may arrive named or positional. */
const field = (res: any, name: string, idx: number): bigint =>
  big(res?.[name] ?? res?.[idx])

/**
 * Returns a [converter, expectedCallCount] tuple for Liquity-family user data.
 *
 * Layout mirrors `buildLiquityUserCall` (reconstructed from the discovery
 * cache + branch config — both deterministic within the request). Emits one
 * `UserData` per BRANCH key `<LENDER>_<chainId>_<COLL_INDEX>`, each containing:
 *
 *  - one sub-account PER TROVE (`accountId` = decimal troveId): collateral
 *    row (`entireColl`) + stable debt row (`entireDebt`, incl. accrued
 *    interest / batch fee / redistribution) with `troveInfo` detail (user-set
 *    rate, zombie flag, rate-adjust cooldown anchor). Troves whose NFT was
 *    transferred away (ownerOf ≠ account) are skipped.
 *  - an `sp` sub-account when the user has Stability Pool state: stable
 *    deposits + yield on the loan row, claimable collateral (liquidation
 *    gains + stash + surplus) on the collateral row, with `spInfo` detail.
 *    SP deposits never collateralize (public data flags the loan market
 *    `collateralDisabled`), so health stays null there.
 */
export const getLiquityUserDataConverter = (
  lender: string,
  chainId: string,
  account: string,
  meta?: LenderToLenderCrossPoolMeta,
): [(data: any[]) => { [lender: string]: UserData } | undefined, number] => {
  const cfg = liquityConfigFor(lender, chainId)
  const branches = liquityBranchesByChain(lender, chainId)
  const discovery = getCachedLiquityTroves(chainId, lender, account)

  const expected =
    cfg && discovery
      ? branches.reduce(
          (acc, b, i) =>
            acc +
            liquityBranchCallCount(b, discovery.perBranch[i]?.length ?? 0),
          0,
        )
      : 0

  return [
    (data: any[]) => {
      if (!cfg || !discovery || expected === 0) return undefined
      if (!data || data.length < expected) return undefined

      const stableAddr = cfg.boldToken.toLowerCase()
      const stableDecimals = cfg.boldDecimals ?? 18
      const accountLower = account.toLowerCase()
      const out: { [lender: string]: UserData } = {}

      let cursor = 0
      branches.forEach((branch, bi) => {
        const troves = discovery.perBranch[bi] ?? []
        const callCount = liquityBranchCallCount(branch, troves.length)
        const slice = data.slice(cursor, cursor + callCount)
        cursor += callCount

        const lenderKey = liquityLenderKey(lender, chainId, branch.collIndex)
        const metaMap: LenderCrossPoolMeta | undefined = meta?.[lenderKey]
        if (!metaMap) return

        const collAddr = branch.collToken.toLowerCase()
        const collUid = createMarketUid(chainId, lenderKey, collAddr)
        const loanUid = createMarketUid(chainId, lenderKey, stableAddr)
        const collMeta = metaMap[collUid]
        const loanMeta = metaMap[loanUid]
        const collDecimals = collMeta?.asset?.decimals ?? branch.collDecimals
        const collDisplay = collMeta ? getDisplayPrice(collMeta) : 0
        const collOracle = collMeta ? getOraclePrice(collMeta) : 0
        const collHist = collMeta?.price?.priceUsd24h ?? collDisplay
        const loanDisplay = loanMeta ? getDisplayPrice(loanMeta) : 0
        const loanOracle = loanMeta ? getOraclePrice(loanMeta) : 0
        const loanHist = loanMeta?.price?.priceUsd24h ?? loanDisplay

        const lendingPositions: Record<string, any> = {}
        const modes: Record<string, number> = {}
        const hist: Record<
          string,
          { totalDeposits24h: number; totalDebt24h: number }
        > = {}

        troves.forEach((t, ti) => {
          const troveData = slice[ti * 2]
          const ownerRaw = slice[ti * 2 + 1]
          const owner =
            typeof ownerRaw === 'string' ? ownerRaw.toLowerCase() : undefined
          // NFT transferred away → the position belongs to someone else now.
          if (owner !== accountLower) return
          if (!troveData || troveData === '0x') return

          const entireDebt = field(troveData, 'entireDebt', 0)
          const entireColl = field(troveData, 'entireColl', 1)
          if (entireDebt === 0n && entireColl === 0n) return

          const troveInfo: LiquityTroveInfo = {
            troveId: t.troveId,
            collIndex: branch.collIndex,
            annualInterestRate: field(
              troveData,
              'annualInterestRate',
              6,
            ).toString(),
            lastInterestRateAdjTime: Number(
              field(troveData, 'lastInterestRateAdjTime', 9),
            ),
            zombie: t.zombie,
            redistCollGain: field(troveData, 'redistCollGain', 3).toString(),
            redistBoldDebtGain: field(
              troveData,
              'redistBoldDebtGain',
              2,
            ).toString(),
            accruedInterest: field(troveData, 'accruedInterest', 4).toString(),
            accruedBatchManagementFee: field(
              troveData,
              'accruedBatchManagementFee',
              8,
            ).toString(),
          }

          const collStr = parseRawAmount(entireColl.toString(), collDecimals)
          const collNum = Number(collStr)
          const debtStr = parseRawAmount(entireDebt.toString(), stableDecimals)
          const debtNum = Number(debtStr)

          lendingPositions[t.troveId] = {
            [collUid]: {
              marketUid: collUid,
              underlying: collAddr,
              deposits: collStr,
              debt: '0',
              debtStable: '0',
              depositsUSD: collNum * collDisplay,
              debtUSD: 0,
              debtStableUSD: 0,
              depositsUSDOracle: collNum * collOracle,
              debtUSDOracle: 0,
              debtStableUSDOracle: 0,
              stableBorrowRate: '0',
              collateralEnabled: true,
              claimableRewards: 0,
            },
            [loanUid]: {
              marketUid: loanUid,
              underlying: stableAddr,
              deposits: '0',
              debt: debtStr,
              debtStable: '0',
              depositsUSD: 0,
              debtUSD: debtNum * loanDisplay,
              debtStableUSD: 0,
              depositsUSDOracle: 0,
              debtUSDOracle: debtNum * loanOracle,
              debtStableUSDOracle: 0,
              stableBorrowRate: '0',
              collateralEnabled: false,
              claimableRewards: 0,
              troveInfo,
            },
          }
          /**
           * The trove's OWN interest rate — the borrower picks it at open and
           * it is the parameter `config.openParameter` describes.
           *
           * Liquity is the MUTABLE case: the rate is adjustable in place, but
           * rate-limited by `interestRateAdjCooldownSeconds` and fee-charged if
           * adjusted early — hence `immutableAfterOpen: false` on the config
           * rather than an unconditional toggle.
           *
           * Emitted as a WAD fraction, matching `annualInterestRate` on
           * `troveInfo`; a consumer reads `openParameter.kind` to know that is
           * what this number means rather than an e-mode category.
           */
          modes[t.troveId] = Number(troveInfo.annualInterestRate) / 1e18
          hist[t.troveId] = {
            totalDeposits24h: collNum * collHist,
            totalDebt24h: debtNum * loanHist,
          }
        })

        // Stability-pool ("earn") position + claimable collateral.
        const spBase = troves.length * 2
        const spDeposit = big(slice[spBase])
        const spYield = big(slice[spBase + 1])
        const spCollGain = big(slice[spBase + 2])
        const spStashed = big(slice[spBase + 3])
        const collSurplus = branch.collSurplusPool ? big(slice[spBase + 4]) : 0n

        const spStable = spDeposit + spYield
        const spColl = spCollGain + spStashed + collSurplus
        if (spStable > 0n || spColl > 0n) {
          const spInfo: LiquitySpInfo = {
            deposit: spDeposit.toString(),
            yieldGain: spYield.toString(),
            collGain: spCollGain.toString(),
            stashedColl: spStashed.toString(),
            collSurplus: collSurplus.toString(),
          }
          const spStableStr = parseRawAmount(
            spStable.toString(),
            stableDecimals,
          )
          const spStableNum = Number(spStableStr)
          const spCollStr = parseRawAmount(spColl.toString(), collDecimals)
          const spCollNum = Number(spCollStr)

          lendingPositions['sp'] = {
            [loanUid]: {
              marketUid: loanUid,
              underlying: stableAddr,
              deposits: spStableStr,
              debt: '0',
              debtStable: '0',
              depositsUSD: spStableNum * loanDisplay,
              debtUSD: 0,
              debtStableUSD: 0,
              depositsUSDOracle: spStableNum * loanOracle,
              debtUSDOracle: 0,
              debtStableUSDOracle: 0,
              stableBorrowRate: '0',
              collateralEnabled: false,
              claimableRewards: 0,
              spInfo,
            },
            ...(spColl > 0n
              ? {
                  [collUid]: {
                    marketUid: collUid,
                    underlying: collAddr,
                    deposits: spCollStr,
                    debt: '0',
                    debtStable: '0',
                    depositsUSD: spCollNum * collDisplay,
                    debtUSD: 0,
                    debtStableUSD: 0,
                    depositsUSDOracle: spCollNum * collOracle,
                    debtUSDOracle: 0,
                    debtStableUSDOracle: 0,
                    stableBorrowRate: '0',
                    collateralEnabled: false,
                    claimableRewards: 0,
                  },
                }
              : {}),
          }
          modes['sp'] = 0
          hist['sp'] = {
            totalDeposits24h: spStableNum * loanHist + spCollNum * collHist,
            totalDebt24h: 0,
          }
        }

        if (Object.keys(lendingPositions).length === 0) return

        const userData = createMultiAccountTypeUserState(
          {
            chainId,
            account,
            lendingPositions,
            modes,
          } as any,
          metaMap,
          hist,
        )

        if (userData && userData.data.length > 0) out[lenderKey] = userData
      })

      return Object.keys(out).length > 0 ? out : undefined
    },
    expected,
  ]
}
